'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  BriefcaseIcon,
  Mail,
  CheckCircle,
  AlertCircle,
  Copy,
  EditIcon,
  TrashIcon,
  CheckCircle2,
  FolderLockIcon,
  MailCheckIcon,
  PhoneIcon,
  ShieldCheckIcon,
  ShieldOffIcon
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { ContentDetailInfo, DeleteConfirmationDialog, UserAvatar, ConfirmationDialog } from '@/components/partials'
import { SendEmailVerificationDialog, UserAddresses } from '@/components/partials/users'
import { formatDateTime, formatDate, getErrorMessage } from '@/lib/utils'
import { hasPermission, hasAnyPermission } from '@/lib/permissions'
import { useCopyToClipboard, useDeleteUser, useUpdateUser, useUser } from '@/hooks'
import { useMeStore } from '@/store'

type PropsType = {
  id: string
}

const UserDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-8 w-16" />
      <div>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-1 h-4 w-32" />
      </div>
    </div>

    <Card>
      <CardContent className="p-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="mt-1 h-4 w-40" />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-20" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const { data: user, isLoading, error } = useUser(id)
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null)
  const [sendVerificationOpen, setSendVerificationOpen] = useState(false)
  const [approvalConfirm, setApprovalConfirm] = useState<boolean | null>(null)
  const { mutate: deleteUserMutate, isPending: isDeleting } = useDeleteUser()
  const { mutate: updateUserMutate, isPending: isUpdating } = useUpdateUser()
  const { meState } = useMeStore()
  const currentUser = meState.response

  const canSendVerification =
    hasPermission(currentUser, 'USERS', 'SHOW') || hasPermission(currentUser, 'USERS', 'UPDATE')

  const canUpdateUser = hasPermission(currentUser, 'USERS', 'UPDATE')

  const handleApproval = (approved: boolean) => {
    setApprovalConfirm(approved)
  }

  const confirmApproval = () => {
    if (approvalConfirm === null) {
      return
    }
    updateUserMutate(
      { userId: id, params: { approved: approvalConfirm } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['user', id] })
          toast.success(approvalConfirm ? t('user_approved_successfully') : t('user_approval_revoked'))
          setApprovalConfirm(null)
        },
        onError: (error: any) => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
          setApprovalConfirm(null)
        }
      }
    )
  }

  const confirmDeleteUser = () => {
    if (deleteUserId) {
      deleteUserMutate(deleteUserId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          toast.success(t('deleted_successfully'))
          router.push('/users')
        },
        onError: error => {
          console.error('Error deleting user:', error)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <UserDetailSkeleton />
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <h3 className="mb-2 text-lg font-semibold">{t('error')}</h3>
            <p className="text-muted-foreground mb-4">{getErrorMessage(error, t('error_loading_record'))}</p>
            <Button onClick={() => router.push('/users')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" asChild>
            <Link href="/users">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{user.fullName}</h1>
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground text-sm">ID: {user.id}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(user.id, t('copied'))}
                className="h-6 w-6 p-0">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {canSendVerification && !user.emailVerifiedAt && (
            <Button variant="outline" onClick={() => setSendVerificationOpen(true)}>
              <MailCheckIcon className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('send_email_verification')}</span>
            </Button>
          )}
          {canUpdateUser &&
            (user.approvedAt ? (
              <Button variant="outline" onClick={() => handleApproval(false)} disabled={isUpdating}>
                <ShieldOffIcon className="h-4 w-4" />
                <span className="hidden items-center gap-2 lg:flex">{t('revoke_approval')}</span>
              </Button>
            ) : (
              <Button variant="outline" onClick={() => handleApproval(true)} disabled={isUpdating}>
                <ShieldCheckIcon className="h-4 w-4" />
                <span className="hidden items-center gap-2 lg:flex">{t('approve')}</span>
              </Button>
            ))}
          <Button variant="outline" asChild>
            <Link href={`/users/${user.id}/edit`}>
              <EditIcon />
              <span className="hidden items-center gap-2 lg:flex">{t('edit_user')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={() => setDeleteUserId(id)}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-5">
          <div className="mb-10 flex items-center space-x-4">
            <UserAvatar user={user} size="lg" />
            <div>
              <h3 className="font-semibold">{user.fullName}</h3>
              <div className="text-muted-foreground flex items-center gap-1 text-sm">
                <Mail className="h-3 w-3" />
                <span>{user.email}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(user.email, t('copied'))}
                  className="ml-1 h-5 w-5 p-0">
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              {user.emailVerifiedAt && (
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  {t('email_verified')}: {formatDateTime(user.emailVerifiedAt, locale)}
                </div>
              )}
              {user.blockedAt && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-3 w-3" />
                  {t('blocked')}: {formatDateTime(user.blockedAt, locale)}
                </div>
              )}
              {user.approvedAt ? (
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <ShieldCheckIcon className="h-3 w-3" />
                  {t('approved_at')}: {formatDateTime(user.approvedAt, locale)}
                  {user.approvedUser && (
                    <span className="text-muted-foreground ml-1">({user.approvedUser.fullName})</span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-sm text-yellow-600">
                  <ShieldOffIcon className="h-3 w-3" />
                  {t('pending_approval')}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('status')}:</span>
              <Badge
                variant="outline"
                className={
                  user.online
                    ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                }>
                {t(user.online ? 'online' : 'offline')}
              </Badge>
            </div>
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('roles')}:</span>
              <div className="flex flex-wrap gap-1">
                {user.roles.map((role: string) => (
                  <Badge key={role} variant="outline" className="text-xs">
                    {t(role)}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('groups')}:</span>
              <div className="flex flex-wrap gap-1">
                {user.groups.length > 0 ? (
                  user.groups.map(group => (
                    <Link href={`/users/groups/${group.id}`} key={group.id}>
                      <Badge variant="outline" className="text-xs">
                        {group.name}
                      </Badge>
                    </Link>
                  ))
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
            </div>
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('gender')}:</span>
              <Badge variant="outline">{t(user.gender)}</Badge>
            </div>
            {user.company && (
              <div className="flex justify-start space-x-2 text-sm">
                <BriefcaseIcon className="text-muted-foreground h-4 w-4" />
                <span>{user.company}</span>
              </div>
            )}
            {user.phone && (
              <div className="flex justify-start space-x-2 text-sm">
                <PhoneIcon className="text-muted-foreground h-4 w-4" />
                <span>{user.phone}</span>
              </div>
            )}
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('birth_date')}:</span>
              <span>{formatDate(user.birthDate, locale)}</span>
            </div>
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('timezone')}:</span>
              <span className="font-mono">{user.timezone}</span>
            </div>
            <div className="flex justify-start space-x-2 text-sm">
              <span>{t('discount_rate')}:</span>
              <span>{user.discountRate}%</span>
            </div>
            {user.currencyCode && (
              <div className="flex justify-start space-x-2 text-sm">
                <span>{t('currency')}:</span>
                <span className="font-mono">{user.currencyCode}</span>
              </div>
            )}
          </div>
          <Separator />
          <ContentDetailInfo entity={user} />
        </CardContent>
      </Card>

      {hasAnyPermission(currentUser, [['ADDRESSES', 'LIST']]) && <UserAddresses userId={id} />}

      {/* All Permissions Card (Groups + Specific) */}
      <Card>
        <CardHeader className="flex flex-row space-x-1">
          <FolderLockIcon />
          <h2 className="text-lg font-semibold">{t('permissions')}</h2>
        </CardHeader>
        <CardContent>
          {user.permissions && user.permissions.length > 0 ? (
            <div className="space-y-4">
              {user.permissions.map((permission: any) => (
                <div key={permission.id} className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-semibold">{t(permission.name.toLowerCase())}</h3>
                  </div>
                  <Separator className="mb-3" />
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-5">
                    {permission.actions.map((action: any) => (
                      <div key={action.id} className="flex items-center justify-items-start space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{t(`permission_actions_${action.name.toLowerCase()}`)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4" />
              {t('no_permissions_assigned')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Specific Permissions Card */}
      <Card>
        <CardHeader className="flex flex-row space-x-1">
          <FolderLockIcon />
          <h2 className="text-lg font-semibold">{t('user_specific_permissions')}</h2>
        </CardHeader>
        <CardContent>
          {user.specificPermissions && user.specificPermissions.length > 0 ? (
            <div className="space-y-4">
              {user.specificPermissions.map((permission: any) => (
                <div key={permission.id} className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-semibold">{t(permission.name.toLowerCase())}</h3>
                  </div>
                  <Separator className="mb-3" />
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 xl:grid-cols-5">
                    {permission.actions.map((action: any) => (
                      <div key={action.id} className="flex items-center justify-items-start space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{t(`permission_actions_${action.name.toLowerCase()}`)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4" />
              {t('no_specific_permissions_assigned')}
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteUserId !== null}
        onCancel={() => setDeleteUserId(null)}
        onConfirm={confirmDeleteUser}
        isDeleting={isDeleting}
      />

      <ConfirmationDialog
        open={approvalConfirm !== null}
        onCancel={() => setApprovalConfirm(null)}
        onConfirm={confirmApproval}
        isLoading={isUpdating}
        description={approvalConfirm ? t('approve_confirmation') : t('revoke_approval_confirmation')}
      />

      <SendEmailVerificationDialog
        userId={id}
        userRoles={user.roles}
        open={sendVerificationOpen}
        onOpenChange={setSendVerificationOpen}
      />
    </div>
  )
}

export default Detail
